import { useCallback, useRef, useState } from 'react';
import { IExcelParseResult } from '../utils';

/**
 * Excel 解析状态枚举
 */
export const enum ExcelParseStatus {
  /** 空闲 */
  IDLE = 'IDLE',
  /** 解析中 */
  RUNNING = 'RUNNING',
  /** 解析成功 */
  SUCCESS = 'SUCCESS',
  /** 解析失败 */
  ERROR = 'ERROR',
  /** 解析超时 */
  TIMEOUT = 'TIMEOUT',
}

/**
 * 多 Sheet 解析结果接口
 */
export interface IExcelAllSheetsResult {
  /** 所有工作表的解析结果 */
  sheets: IExcelParseResult[];
  /** 工作表名称列表 */
  sheetNames: string[];
}

/** Excel 解析最大行数限制 */
const MAX_EXCEL_ROWS = 10000;

/**
 * 检测值的类型
 */
const detectValueType = (value: any): 'number' | 'date' | 'text' => {
  if (value === null || value === undefined || value === '') {
    return 'text';
  }

  if (value instanceof Date) {
    return 'date';
  }

  if (typeof value === 'number' && !isNaN(value)) {
    return 'number';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return 'number';
    }

    const datePatterns = [
      /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/,
      /^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/,
      /^\d{4}[-/]\d{1,2}[-/]\d{1,2}\s+\d{1,2}:\d{2}(:\d{2})?$/,
      /^\d{4}年\d{1,2}月\d{1,2}日$/,
    ];

    for (const pattern of datePatterns) {
      if (pattern.test(trimmed)) {
        return 'date';
      }
    }
  }

  return 'text';
};

/**
 * 检测列的数据类型
 */
const detectColumnType = (
  colIndex: number,
  dataRows: (string | number | null)[][],
  sampleSize = 10
): 'number' | 'date' | 'text' => {
  const typeCounts: Record<string, number> = { number: 0, date: 0, text: 0 };
  const samplesToCheck = Math.min(sampleSize, dataRows.length);
  let validValueCount = 0;

  for (let i = 0;i < samplesToCheck;i++) {
    const value = dataRows[i]?.[colIndex];
    if (value === null || value === undefined || value === '') {
      continue;
    }
    // eslint-disable-next-line no-plusplus
    validValueCount++;
    const valueType = detectValueType(value);
    // eslint-disable-next-line no-plusplus
    typeCounts[valueType]++;
  }

  if (validValueCount === 0) {
    return 'text';
  }

  const threshold = validValueCount * 0.5;
  if (typeCounts.number >= threshold) {
    return 'number';
  }
  if (typeCounts.date >= threshold) {
    return 'date';
  }

  return 'text';
};

/**
 * 将 Date 对象格式化为 yyyy-MM-dd 字符串
 * 兜底处理：当 xlsx 仍返回 Date 对象时使用
 */
const formatDateValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

/**
 * 在主线程中解析 Excel ArrayBuffer（所有工作表）
 * 使用动态 import('xlsx') 加载本地 npm 依赖，不依赖外部 CDN，不受 CSP 限制
 */
const parseExcelInMainThread = async (
  arrayBuffer: ArrayBuffer,
): Promise<IExcelAllSheetsResult> => {
  const XLSX = await import('xlsx');

  const workbook = XLSX.read(arrayBuffer, {
    type: 'array',
    cellDates: false,   // 不将日期转为 Date 对象，保留原始数值
    cellNF: true,       // 保留单元格的数字格式字符串
    cellText: false,
  });

  const sheetNames = workbook.SheetNames;

  /** 解析单个工作表 */
  const parseSheet = (sheetName: string, maxRows = MAX_EXCEL_ROWS): IExcelParseResult => {
    const worksheet = workbook.Sheets[sheetName];

    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: null,
      blankrows: true,
      raw: false,  // 使用格式化后的值，确保日期按原始格式展示
    });

    if (jsonData.length === 0) {
      return { tableData: [], columns: [], sheetName };
    }

    // 使用第一行来确定列数
    const firstRow = jsonData[0] as (string | number | null)[];
    // 数据类型检测使用第二行开始的数据（跳过表头行）
    const dataRowsForDetection = jsonData.slice(1) as (string | number | null)[][];

    // 总行数（包含表头行）
    const originalRowCount = jsonData.length;
    const isTruncated = originalRowCount > maxRows;
    const actualRowCount = isTruncated ? maxRows : originalRowCount;

    if (isTruncated) {
      console.warn(
        `Excel 工作表「${sheetName}」数据行数 ${originalRowCount} 超过最大限制 ${maxRows}，已截取前 ${maxRows} 行`,
      );
    }

    // 生成列配置（使用字母作为标题，因为 SmartTableGrid 使用 A/B/C 字母表头）
    // 原始表头行会作为 tableData 的第一行数据保留
    const columns = firstRow.map((_header: any, index: number) => {
      const columnType = detectColumnType(index, dataRowsForDetection);
      const sortable = columnType === 'number' || columnType === 'date';

      return {
        data: `col${index + 1}`,
        title: '',
        width: 150,
        sortable,
        type: 'text' as const,
      };
    });

    // 生成数据行：从第一行（原始表头）开始，全部作为 tableData
    const tableData: Record<string, any>[] = [];
    for (let i = 0;i < actualRowCount;i++) {
      const rowData = jsonData[i] as any[];
      const row: Record<string, any> = {};
      columns.forEach((col: any, colIndex: number) => {
        const value = rowData?.[colIndex];
        // 兜底处理：如果仍有 Date 对象，转为 yyyy-MM-dd HH:mm:ss 格式
        if (value instanceof Date) {
          row[col.data] = formatDateValue(value);
        } else {
          row[col.data] = value !== undefined ? value : null;
        }
      });
      tableData.push(row);
    }

    return {
      tableData,
      columns,
      sheetName,
      isTruncated,
      originalRowCount,
    };
  };

  const sheets: IExcelParseResult[] = sheetNames.map((name: string) => parseSheet(name));
  return { sheets, sheetNames };
};

/**
 * Excel Hook 配置选项
 */
export interface IUseExcelWorkerOptions {
  /** 超时时间（毫秒），默认 60 秒 */
  timeout?: number;
  /** 错误回调 */
  onError?: (error: Error) => void;
}

/**
 * Hook 返回类型
 */
export type UseExcelWorkerReturn = [
  /** 解析函数 */
  (arrayBuffer: ArrayBuffer) => Promise<IExcelAllSheetsResult>,
  /** 当前解析状态 */
  ExcelParseStatus,
  /** 取消解析 */
  (status?: ExcelParseStatus) => void,
];

/**
 * Excel 解析 Hook（主线程模式）
 *
 * 使用动态 import('xlsx') 在主线程解析 Excel 文件：
 *
 * @param options - 配置选项
 * @returns [parseFn, status, cancel]
 *
 * @example
 * ```tsx
 * const [parseExcel, parseStatus, cancelParse] = useExcelWorker();
 * const result = await parseExcel(arrayBuffer);
 * ```
 */
const useExcelWorker = (options: IUseExcelWorkerOptions = {}): UseExcelWorkerReturn => {
  const { timeout = 60000, onError } = options;

  const [status, setStatus] = useState<ExcelParseStatus>(ExcelParseStatus.IDLE);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef(false);

  /** 取消解析 */
  const cancel = useCallback((newStatus: ExcelParseStatus = ExcelParseStatus.IDLE) => {
    abortRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setStatus(newStatus);
  }, []);

  /** 解析 Excel */
  const parseExcel = useCallback(
    async (arrayBuffer: ArrayBuffer): Promise<IExcelAllSheetsResult> => {
      if (status === ExcelParseStatus.RUNNING) {
        const error = new Error('解析正在进行中');
        onError?.(error);
        return Promise.reject(error);
      }

      abortRef.current = false;
      setStatus(ExcelParseStatus.RUNNING);

      return new Promise<IExcelAllSheetsResult>((resolve, reject) => {
        // 超时控制
        timeoutRef.current = setTimeout(() => {
          abortRef.current = true;
          const timeoutError = new Error('Timeout');
          onError?.(timeoutError);
          setStatus(ExcelParseStatus.TIMEOUT);
          reject(timeoutError);
        }, timeout);

        // 主线程解析
        parseExcelInMainThread(arrayBuffer)
          .then((result) => {
            if (abortRef.current) return;
            clearTimeout(timeoutRef.current);
            setStatus(ExcelParseStatus.SUCCESS);
            resolve(result);
          })
          .catch((error) => {
            if (abortRef.current) return;
            clearTimeout(timeoutRef.current);
            const err = error instanceof Error ? error : new Error(String(error));
            onError?.(err);
            setStatus(ExcelParseStatus.ERROR);
            reject(err);
          });
      });
    },
    [status, timeout, onError],
  );

  return [parseExcel, status, cancel];
};

/** 解析 Excel 的便捷方法类型 */
export type TParseExcelFn = (arrayBuffer: ArrayBuffer) => Promise<IExcelAllSheetsResult>;

export default useExcelWorker;
